





















































































import { Component, Emit, InjectReactive, Vue, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { FeedTank } from '@/api';
import { CorrectionService } from '@/services/correction-service';
import Toast from '@/components/display/toast.vue';

type EventType = 'refill' | 'estimate' | 'empty';

interface FormState {
	eventType: EventType;
	date: string;
	time: string;
	beforeValueTonnes: number;
	variance: number;
	changeTonnes: number;
	notes: string;
}

@Component({})
export default class AddFeedTankReferencePoint extends Vue {
	@InjectReactive('selectedFeedTank')
	public feedTank!: FeedTank;

	public eventTypeOptions: Array<{ value: EventType; text: string }> = [
		{ value: 'refill', text: 'Refill' },
		{ value: 'estimate', text: 'Estimate' },
		{ value: 'empty', text: 'Empty' },
	];
	public varianceOptions = [
		{ value: 1, text: '±1' },
		{ value: 2, text: '±2' },
		{ value: 3, text: '±3' },
	];
	public form: FormState = {
		eventType: 'refill',
		date: moment().format('YYYY-MM-DD'),
		time: moment().format('HH:mm'),
		beforeValueTonnes: 0,
		variance: 1,
		changeTonnes: 0,
		notes: '',
	};
	public submitting = false;

	public get maxDate(): string {
		return moment().format('YYYY-MM-DD');
	}

	private toast: Toast = new Toast();

	@Watch('form.eventType')
	public onEventTypeChange(newVal: EventType): void {
		if (newVal === 'empty') {
			this.form.time = moment().format('HH:mm');
		}
	}

	public get tableFields() {
		return [
			{ key: 'label', label: 'Field', tdClass: 'align-middle' },
			{ key: 'value', label: 'Value', tdClass: 'align-middle' },
		];
	}

	public get tableRows(): { key: string; label: string }[] {
		const rows: { key: string; label: string }[] = [
			{ key: 'date', label: 'Date' },
		];
		if (this.form.eventType === 'empty') {
			rows.push({ key: 'time', label: 'Time' });
		}
		if (this.showBeforeValue) {
			rows.push({
				key: 'beforeValue',
				label: this.form.eventType === 'estimate' ? 'Estimated weight (tonnes)' : 'Weight before (tonnes)',
			});
		}
		if (this.showVariance) {
			rows.push({ key: 'variance', label: 'Variance (tonnes)' });
		}
		if (this.showChanges) {
			rows.push({ key: 'change', label: 'Change (tonnes)' });
			rows.push({ key: 'afterValueComputed', label: 'Weight after (tonnes)' });
		}
		return rows;
	}

	public get showBeforeValue(): boolean {
		return this.form.eventType === 'refill' || this.form.eventType === 'estimate';
	}
	public get showVariance(): boolean {
		return this.form.eventType === 'refill' || this.form.eventType === 'estimate';
	}
	public get showChanges(): boolean {
		return this.form.eventType === 'refill';
	}

	public get computedAfterValue(): string {
		const before = this.form.beforeValueTonnes ?? 0;
		const change = this.form.changeTonnes ?? 0;
		return (before + change).toFixed(2);
	}

	public get canSubmit(): boolean {
		if (this.form.eventType === 'empty') return true;
		if (this.form.eventType === 'estimate') {
			return (
				typeof this.form.beforeValueTonnes === 'number' &&
				[1, 2, 3].indexOf(this.form.variance) !== -1
			);
		}
		// refill: changes cannot be 0
		return (
			typeof this.form.beforeValueTonnes === 'number' &&
			[1, 2, 3].indexOf(this.form.variance) !== -1 &&
			typeof this.form.changeTonnes === 'number' &&
			this.form.changeTonnes !== 0
		);
	}

	public async submit(): Promise<void> {
		if (!this.feedTank?.id) {
			this.toast.makeToast('Feed tank not available', 'Error', 'danger');
			return;
		}
		if (this.form.eventType === 'refill' && (this.form.changeTonnes == null || this.form.changeTonnes === 0)) {
			this.toast.makeToast('Changes cannot be 0 for refill', 'Error', 'danger');
			return;
		}
		this.submitting = true;
		try {
			const { eventType } = this.form;
			const eventTimestamp =
				eventType === 'empty'
					? moment(`${this.form.date} ${this.form.time || moment().format('HH:mm')}`, 'YYYY-MM-DD HH:mm').utc()
					: moment(this.form.date, 'YYYY-MM-DD').endOf('day').utc();

			const weightBeforeKg = eventType === 'empty' ? 0 : Math.round((this.form.beforeValueTonnes ?? 0) * 1000);
			const variance = eventType === 'empty' ? 0 : ([1, 2, 3].includes(this.form.variance) ? this.form.variance * 1000 : 1000);
			// 0 for estimate and empty; from form only for refill
			const weightChangeKg = eventType === 'refill' ? Math.round((this.form.changeTonnes ?? 0) * 1000) : 0;

			const payload = {
				event_timestamp: eventTimestamp.toISOString(),
				event_type: eventType,
				weight_before: weightBeforeKg,
				weight_before_variance: variance,
				weight_change: weightChangeKg,
				notes: this.form.notes?.trim() || null,
			};

			const { error } = await CorrectionService.createFeedTankReferencePoint(this.feedTank.id, payload);
			if (error) {
				this.toast.makeToast(error, 'Error', 'danger');
				return;
			}

			this.toast.makeToast('Reference point recorded', 'Success', 'success');
			this.form.beforeValueTonnes = 0;
			this.form.variance = 1;
			this.form.changeTonnes = 0;
			this.form.notes = '';
			this.form.date = moment().format('YYYY-MM-DD');
			this.form.time = moment().format('HH:mm');
			this.onUpdateReferencePoints();
		} finally {
			this.submitting = false;
		}
	}

	@Emit('updateReferencePoints')
	public onUpdateReferencePoints(): void {}
}
